import { type SetOptional } from '@frontend/duck-tape';
import { safeCss } from '@frontend/web-utils';
import type { InputBaseProps as MInputBaseProps } from '@mantine/core';
import { InputBase } from '@mantine/core';
import * as LPN from 'libphonenumber-js';
import { IMaskInput } from 'react-imask';
import type { InputLabelProps } from '../../InputLabel';
import { InputLabel } from '../../InputLabel';
import type { MakeInputProps } from '../types';

export type PhoneNumberInputProps = MakeInputProps<string, MInputBaseProps, 'labelProps'> & {
  labelProps?: SetOptional<InputLabelProps, 'label'>;
  labelTextProps?: InputLabelProps['textProps'];
  showOptionalText?: boolean;
};

export const PhoneNumberInput = ({
  label = 'Phone number',
  labelProps,
  labelTextProps,
  onChange,
  showOptionalText,
  ...props
}: PhoneNumberInputProps) => {
  const classNames: MInputBaseProps['classNames'] = {
    input: safeCss('text-p-md h-[56px] rounded-sm focus-within:border-textPrimary'),
    root: safeCss('gap-y-sm flex flex-col flex-1'),
  };

  return (
    <InputBase
      aria-label="Phone number"
      autoComplete="tel"
      classNames={classNames}
      component={IMaskInput}
      inputMode="numeric"
      label={
        label ? (
          <InputLabel label={label} showOptionalText={showOptionalText} {...labelProps} textProps={labelTextProps} />
        ) : null
      }
      mask="+1 000-000-0000"
      // Using `onAccept` instead of `onChange` because `react-imask` reliably triggers `onAccept` for both typing
      // and pasting, ensuring the phone number handling logic always runs correctly.
      onAccept={(value) => {
        const stripped = value.replace(/\D/g, '');
        if (!stripped || stripped === '1') {
          onChange('');
          return;
        }
        try {
          const phoneNumber = LPN.parsePhoneNumber(value, 'US');
          onChange(phoneNumber.format('E.164'));
        } catch (error) {
          onChange(value);
        }
      }}
      placeholder="+1 000-000-0000"
      {...props}
    />
  );
};
